import React, { useEffect, useState, useRef } from 'react'
import styles from './styles.module.scss'
import responsiveStyles from './responsiveStyles.module.scss'
import { Wrapper, FCAUpdateBox } from '../../reusable'
import { ContactDetails, Person } from '../../formParts'
import { fieldValidation, uploadDataToServer } from '../../../helpers'
import { Complete } from '../../formParts/complete'
import { useUpdateClaimData, useClaimData } from '../../../claimProvider'
import { SignatureSentinel } from '../../formParts/signatureSentinel'
import { AddressDetails } from '../../formParts/addressDetails'
import { ConfirmDetails } from '../../formParts/confirmDetails'
import { useNavigate } from 'react-router-dom'
import NewsBanner from '../../newsBanner'
import { ft, gbn, itv, skyNews, theSun, bbc, dm } from '../../../assets/newsOutlets'
import { RenderTitleBannerContent } from '../../reusable/tiltBanner'
import ItvBox from '../../reusable/itvBox'

const LEAD_ORIGIN = 'webform_SENTINEL_ALTERNATE'

const newsItems = [
    {
        id: 1,
        // companyName: 'ITV',
        image: itv,
    },
    {
        id: 2,
        // headline: 'As seen on BBC News',
        // companyName: 'BBC News',
        image: bbc,
    },
    {
        id: 3,
        // companyName: 'Sky News',
        image: skyNews,
    },
    {
        id: 4,
        // headline: 'As seen in the Financial Times',
        // companyName: 'Financial Times',
        image: ft,
    },
    {
        id: 5,
        // companyName: 'Daily Mail',
        image: dm,
    },
    {
        id: 6,
        // headline: 'As seen in the Sun',
        // companyName: 'The Sun',
        image: theSun,
    },
    {
        id: 7,
        // companyName: 'GB News',
        image: gbn,
    },
]
const groupsOrder = ['address', 'person', 'contact', 'confirm', 'signature', 'complete']

function ClaimForm() {
    const [groupsComplete, setGroupsComplete] = useState(0)
    const [activeGroup, setActiveGroup] = useState('address')
    const [isFieldEdited, setIsFieldEdited] = useState(false)
    const [hasPressedBack, setHasPressedBack] = useState(false)
    const [displayedPercentage, setDisplayedPercentage] = useState(0)
    const [showInfoBox, setShowInfoBox] = useState(true)
    const navigate = useNavigate()
    const actualPercentage = Math.min(Math.round((groupsComplete / (groupsOrder.length - 1)) * 100), 100)

    const claimData = useClaimData()
    const updateClaim = useUpdateClaimData()

    useEffect(() => {
        if (displayedPercentage === actualPercentage) return

        const isCountingUp = displayedPercentage < actualPercentage

        const animationInterval = setInterval(() => {
            setDisplayedPercentage((prev) => {
                if (isCountingUp) {
                    return prev < actualPercentage ? prev + 1 : actualPercentage
                } else {
                    return prev > actualPercentage ? prev - 1 : actualPercentage
                }
            })
        }, 20)

        return () => clearInterval(animationInterval)
    }, [actualPercentage, displayedPercentage])

    useEffect(() => {
        if (hasPressedBack) {
            setHasPressedBack(false)
        }
    }, [hasPressedBack])

    useEffect(() => {
        if (groupsComplete > 0) {
            setShowInfoBox(false)
        }
    }, [groupsComplete])

    useEffect(() => {
        // Merge (not replace) so this doesn't clobber claimData fields set by
        // other form parts' effects that may commit around the same time.
        updateClaim((prevClaim: Record<string, any>) => ({
            ...prevClaim,
            leadOrigin: LEAD_ORIGIN,
            leadSource: window.location.href,
        }))
    }, [])

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => e.preventDefault()

    const saveFieldValue = (dataToSave: { field: string; value: any }): { success: boolean; error: string } => {
        const validation = fieldValidation(dataToSave)
        if (!validation.success) {
            return validation
        }

        updateClaim((prevClaim: Record<string, any>) => ({
            ...prevClaim,
            [dataToSave.field]: dataToSave.value,
            client_id: prevClaim?.client_id || prevClaim?.leadData?.client_id,
            leadData: prevClaim?.leadData,
        }))

        setIsFieldEdited(true)
        return { success: true, error: '' }
    }

    const activeGroupAssignment = async (group: string) => {
        const currentIndex = groupsOrder.indexOf(activeGroup)
        const targetIndex = groupsOrder.indexOf(group)
        const isGoingBackward = targetIndex < currentIndex

        if (group === 'signature') {
            try {
                let uploadResult

                uploadResult = await uploadDataToServer({
                    ...claimData,
                })
                if (uploadResult.success && uploadResult.responseData) {
                    const responseData = uploadResult.responseData.claimData || uploadResult.responseData
                    // let newClientId = claimData.client_id || responseData.sigData?.client_id || responseData.leadData?.client_id
                    const updatedData = {
                        ...responseData,
                        leadData: {
                            ...responseData.leadData,
                        },
                        leadOrigin: LEAD_ORIGIN,
                        // client_id: newClientId,
                        // client_id: newClientId,
                    }
                    updateClaim(updatedData)
                    setIsFieldEdited(false)
                    setActiveGroup(group)
                    window.scrollTo(0, 0)
                    setGroupsComplete(groupsComplete + 1)
                }
            } catch (error) {
                console.error('Failed to upload claim data:', error)
                return
            }
        } else {
            setActiveGroup(group)
            window.scrollTo(0, 0)

            if (isGoingBackward) {
                setHasPressedBack(true)
                setGroupsComplete(targetIndex)
            } else {
                setGroupsComplete(groupsComplete + 1)
            }
        }
    }

    return (
        <div className={styles.claimFormContainer}>
            <div className={`${styles.claimProgressor} ${responsiveStyles.claimProgressor}`}>
                {/* Title banner */}
                {groupsComplete === 0 && (
                    <RenderTitleBannerContent groupsComplete={groupsComplete} showInfoBox={showInfoBox} />
                )}
                {/* Percentage Progress Indicator */}
                <div
                    className={`${styles.percentageProgressContainer} ${responsiveStyles.percentageProgressContainer}`}
                >
                    <div className={styles.percentageProgressBar}>
                        <div
                            className={styles.percentageProgressFill}
                            style={{
                                width: `${displayedPercentage}%`,
                            }}
                        />
                    </div>
                    <div
                        className={styles.percentageProgressText}
                        style={{
                            left: `${Math.min(Math.max(displayedPercentage, 5), 95)}%`,
                        }}
                    >
                        {displayedPercentage}%
                    </div>
                </div>
                {groupsComplete === 0 && <ItvBox />}
            </div>

            <Wrapper>
                <div className={`${styles.formContainer} ${responsiveStyles.formContainer}`}>
                    <form className={styles.form} onSubmit={handleSubmit} noValidate>
                        <div className={styles.formFields}>
                            <AddressDetails
                                saveValueFunct={saveFieldValue}
                                isActive={activeGroup}
                                assignGroup={activeGroupAssignment}
                            />
                            <Person
                                saveValueFunct={saveFieldValue}
                                isActive={activeGroup}
                                assignGroup={activeGroupAssignment}
                            />
                            <ContactDetails
                                saveValueFunct={saveFieldValue}
                                isActive={activeGroup}
                                assignGroup={activeGroupAssignment}
                            />
                            <ConfirmDetails
                                saveValueFunct={saveFieldValue}
                                isActive={activeGroup}
                                assignGroup={activeGroupAssignment}
                            />
                            <SignatureSentinel
                                saveValueFunct={saveFieldValue}
                                isActive={activeGroup}
                                assignGroup={activeGroupAssignment}
                            />
                            <Complete
                                isActive={activeGroup}
                                saveValueFunct={saveFieldValue}
                                assignGroup={activeGroupAssignment}
                            />
                        </div>
                    </form>
                </div>
            </Wrapper>

            {/* Small title banner  */}
            {groupsComplete > 0 && (
                <div className={styles.smallTitleBanner}>
                    <RenderTitleBannerContent groupsComplete={groupsComplete} showInfoBox={showInfoBox} />
                </div>
            )}

            {/* YouTube Video Section */}
            {groupsComplete === 0 && (
                <div className={styles.videoSection}>
                    <div className={styles.videoTitle}>
                        <span>Sentinel Legal on</span>
                        <img src={skyNews} alt="Sky News" className={styles.skyNewsLogo} />
                    </div>
                    <div className={styles.videoWrapper}>
                        <iframe
                            width="100%"
                            height="100%"
                            src="https://www.youtube.com/embed/vzQ8qgvxV0U"
                            title="Sentinel Legal Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}

            <NewsBanner
                items={newsItems}
                speed="fast"
                height={100}
                backgroundColor="#f8f8f8"
                textColor="#555"
                gradientColors={['rgba(151, 70, 70, 0.95)', 'rgba(132, 49, 49, 0.98)']}
            />

            <FCAUpdateBox />
        </div>
    )
}

export default ClaimForm
